import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Emoji } from '@/components/Emoji';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';
import { COURSES, DIRECTIONS } from '@/lib/data';
import type { Direction, Course } from '@/lib/types';
import { useAppStore } from '@/lib/store';

export function CoursesPage(): JSX.Element {
  const [active, setActive] = useState<Direction | 'all'>('all');
  const completed = useAppStore((s) => new Set(s.progress.completedSentenceIds));
  const customs = useAppStore((s) => s.customCourses);
  const [params] = useSearchParams();
  const justCreated = params.get('just');

  // Materialize custom courses into the same Course shape so they render uniformly.
  const customAsCourses: Course[] = customs.map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    direction: 'custom',
    emoji: c.emoji,
    difficulty: 2,
    estimatedMinutes: c.sentences.length * 2,
    lessons: [{ id: c.id, courseId: c.id, title: c.title, sentences: c.sentences }],
  }));

  const all = [...customAsCourses, ...COURSES];
  const list = active === 'all' ? all : all.filter((c) => c.direction === active);

  return (
    <div className="space-y-10">
      <Reveal>
        <SectionHeading
          eyebrow="Course Market · 课程市场"
          title={<>挑一门， <span className="text-gradient-warm">今天就开始</span></>}
          subtitle="5 大方向 · 持续更新 · 任选 10 个永久免费"
          align="left"
        />
      </Reveal>

      {/* Filter chips + create CTA */}
      <Reveal delay={0.05}>
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <Chip active={active === 'all'} onClick={() => setActive('all')} emoji="✨" label={`全部 ${all.length}`} />
            {DIRECTIONS.map((d) => {
              const count = all.filter((c) => c.direction === d.key).length;
              return (
                <Chip
                  key={d.key}
                  active={active === d.key}
                  onClick={() => setActive(d.key)}
                  emoji={d.emoji}
                  label={`${d.short} ${count}`}
                />
              );
            })}
          </div>
          <GlassButton to="/app/create" size="sm" variant="solid">
            🛠️ 造一节我自己的
          </GlassButton>
        </div>
      </Reveal>

      {justCreated !== null && (
        <Reveal>
          <div className="rounded-3xl border border-mint-400/40 bg-mint-400/[0.08] p-5 flex items-center gap-3">
            <Emoji char="🎉" size={28} />
            <div className="flex-1 text-sm">
              <span className="font-bold text-mint-200">新课已创建</span>
              <span className="text-cream-300 ml-2">已加入"自创"分类，立即可玩。</span>
            </div>
            <Link to={`/app/courses/${justCreated}`} className="text-sm text-honey-300 hover:text-honey-200 shrink-0">
              打开 →
            </Link>
          </div>
        </Reveal>
      )}

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {list.map((c, i) => {
          const totalSentences = c.lessons.reduce((acc, l) => acc + l.sentences.length, 0);
          const done = c.lessons.reduce(
            (acc, l) => acc + l.sentences.filter((s) => completed.has(s.id)).length,
            0,
          );
          const pct = totalSentences === 0 ? 0 : Math.round((done / totalSentences) * 100);
          const dirMeta = DIRECTIONS.find((d) => d.key === c.direction) ?? DIRECTIONS[0]!;
          return (
            <Reveal key={c.id} delay={(i % 6) * 0.05}>
              <Link to={`/app/courses/${c.id}`} className="block h-full">
                <SpotlightCard
                  spotlightColor="rgba(251, 171, 44, 0.22)"
                  className="h-full hover:-translate-y-1 transition-transform"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${dirMeta.gradient}`}>
                      <Emoji char={c.emoji} size={28} />
                    </div>
                    <span className="text-xs text-cream-400">{dirMeta.short}</span>
                  </div>
                  <div className="font-display text-xl font-bold text-white">{c.title}</div>
                  <p className="mt-2 text-sm text-cream-300/90 leading-relaxed">{c.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-cream-400">
                    <span>{c.lessons.length} 节课 · {c.estimatedMinutes} 分钟</span>
                    <span>{'⭐'.repeat(c.difficulty)}</span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-honey-400 to-mint-400"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </SpotlightCard>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  emoji,
  label,
}: {
  active: boolean;
  onClick: () => void;
  emoji: string;
  label: string;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-all border ${
        active
          ? 'bg-honey-400/15 border-honey-400/40 text-honey-100'
          : 'bg-white/[0.03] border-white/[0.06] text-cream-300 hover:bg-white/[0.06] hover:text-white'
      }`}
    >
      <span>{emoji}</span>
      <span>{label}</span>
    </button>
  );
}
